import { useEffect, useRef, useState } from "react";
import { Loader2, SwitchCamera, X } from "lucide-react";

export const LiveCamera = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setReady(false);
    if (!navigator.mediaDevices?.getUserMedia) { setError("This browser can't open the camera. Use Pick screenshot instead."); return undefined; }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((stream) => {
        if (!alive) return stream.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      })
      .catch(() => setError("Camera access was denied. Allow camera in your browser settings, or use Pick screenshot."));
    return () => { alive = false; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, [facing]);

  const snap = () => {
    const v = videoRef.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => b && onCapture(new File([b], "scan.jpg", { type: "image/jpeg" })), "image/jpeg", 0.92);
  };

  return (
    <div className="relative rounded-3xl overflow-hidden bg-black" data-testid="live-camera">
      {error ? <p className="p-6 text-sm text-white" data-testid="live-camera-error">{error}</p> : (
        <video ref={videoRef} playsInline muted onLoadedData={() => setReady(true)} className="w-full aspect-[3/4] object-cover" data-testid="live-camera-video" />
      )}
      {!error && !ready && <Loader2 className="absolute inset-0 m-auto h-6 w-6 animate-spin text-white" />}
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent">
        <button data-testid="live-camera-close" onClick={onClose} aria-label="Close camera" className="h-11 w-11 rounded-full bg-white/15 text-white flex items-center justify-center"><X className="h-5 w-5" /></button>
        <button data-testid="live-camera-snap" onClick={snap} disabled={!ready} aria-label="Take photo" className="h-16 w-16 rounded-full bg-white ring-4 ring-white/40 disabled:opacity-40 active:scale-95 transition-transform" />
        <button data-testid="live-camera-flip" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} aria-label="Switch camera" className="h-11 w-11 rounded-full bg-white/15 text-white flex items-center justify-center"><SwitchCamera className="h-5 w-5" /></button>
      </div>
      {!error && <p className="absolute top-3 inset-x-0 text-center text-xs text-white/80">Fit the chat on screen, hold steady, then tap the button</p>}
    </div>
  );
};
